import React, {ReactElement, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {JS_INTERACTABLE_ELEMENT} from '../../constants';
import {clickedElementIdsSelector, drawEnabledSelector} from '../../store/selectors/presentation.selectors';
import {runFileElementInteractionsSelector} from '../../store/selectors/runfile.selectors';
import {addFontFamilyToHead, defaultFonts} from '../../utils/defaultFonts';
import Element from './element';
import './elements.css';
import {IElementStaticText} from '../../incase-domain/elements/staticText';
import {IElement} from '../../incase-domain/elements';
import {isCoverHtmlElement} from '../../incase-domain/elements/html/html';
import {isHtmlSlottedElement} from '../../incase-domain/elements/html/htmlSlot';
import {useSaveRunfile} from '../../hooks/useSaveRunfile';
import {DeviceType} from '../../incase-domain/common/DeviceType.enum';
import {ELEMENT, interactiveElementTypes} from '../../incase-domain/elements/base/ElementTypes';
import {filterAndSortMobileElements} from '../../util/presentation';
import {ISlide} from '../../incase-domain/slide';
import {useSlidePreviewCapture} from '../../context/SlidePreviewCaptureContext';

interface ElementsProps {
    slide: ISlide;
    device?: DeviceType;
}

/* elements that dont navigate forward when user clicks on them */
const NON_NAVIGATION_ELEMENTS = [
    ...interactiveElementTypes,
    ELEMENT.HTML,
    ELEMENT.VIDEO,
    ELEMENT.FORM,
    ELEMENT.IMAGE_GALLERY,
    ELEMENT.QUESTION_REPORT,
];

function Elements(props: ElementsProps): ReactElement {
    const {slide, device = DeviceType.DESKTOP} = props;

    const clickedElementIds = useSelector(clickedElementIdsSelector);
    const drawEnabled = useSelector(drawEnabledSelector);
    const elementInteractionsBySlide = useSelector(runFileElementInteractionsSelector);
    const isSlidePreviewCapture = useSlidePreviewCapture();

    const savePresentationRunFileHandler = useSaveRunfile();

    useEffect(() => {
        const cleanupFunctions: Array<() => void> = [];

        defaultFonts.forEach((font) => {
            const cleanup = addFontFamilyToHead(font);
            cleanupFunctions.push(cleanup);
        });

        return () => {
            cleanupFunctions.forEach((cleanup) => cleanup());
        };
    }, []);

    useEffect(() => {
        const cleanupFunctions: Array<() => void> = [];
        if (slide.elements) {
            slide.elements.forEach((element) => {
                if (element.type === ELEMENT.FLIP_CARD) {
                    cleanupFunctions.push(addFontFamilyToHead(element.flipCardElement?.FRONT.fontFamily));
                    cleanupFunctions.push(addFontFamilyToHead(element.flipCardElement?.BACK.fontFamily));
                } else {
                    cleanupFunctions.push(addFontFamilyToHead(element.fontFamily));
                }
            });
        }

        return () => {
            cleanupFunctions.forEach((cleanup) => cleanup());
        };
    }, [slide.elements]);

    function shouldDisplay(element: IElement, clickedElementIds: string[]): boolean {
        if (isSlidePreviewCapture) {
            return true;
        }

        if (!element.elementToClickForDisplay || element.elementToClickForDisplay.length === 0) {
            return true;
        }

        return element.elementToClickForDisplay.every((elementId) => clickedElementIds.includes(elementId));
    }

    function isClickable(element: IElement): boolean {
        return !!(
            element.elementToDisplayAfterClick?.length ||
            element.clearAfterClicked ||
            (element as IElementStaticText).slideChange?.slideId
        );
    }

    function getDeviceSpecificElements(): IElement[] {
        const elements = (slide.elements || []).filter((element) => element.type !== ELEMENT.PLACEHOLDER && !isHtmlSlottedElement(element));
        if (device === DeviceType.MOBILE) {
            return filterAndSortMobileElements(elements);
        }

        return elements;
    }

    const hasCoverSlide = device === DeviceType.MOBILE && getDeviceSpecificElements().some((element) => isCoverHtmlElement(element));

    return (
        <div
            style={
                device === DeviceType.MOBILE
                    ? {
                          display: 'flex',
                          flexDirection: 'column',
                          gap: `${slide?.gapBetweenMobileElements}em`,
                          paddingTop: `${slide?.elementPadding?.top}em`,
                          paddingRight: `${slide?.elementPadding?.right}em`,
                          paddingBottom: `${slide?.elementPadding?.bottom}em`,
                          paddingLeft: `${slide?.elementPadding?.left}em`,
                          ...(hasCoverSlide ? {flex: 1, minHeight: 0, height: '100%'} : {}),
                      }
                    : {}
            }
        >
            {getDeviceSpecificElements().map((element) => {
                const slideElementInteractions = elementInteractionsBySlide ? elementInteractionsBySlide[slide._id] : {};
                const visible = shouldDisplay(element, clickedElementIds);
                let elementInteractions;
                if (slideElementInteractions && slideElementInteractions[element.id]) {
                    elementInteractions = slideElementInteractions[element.id];
                } else {
                    elementInteractions = {};
                }
                return (
                    <div
                        key={`element-${slide._id}-${element.id}`}
                        className={`c-slideElement ${!drawEnabled && visible && isClickable(element) ? `u-pointer` : ''}  ${
                            isClickable(element) || NON_NAVIGATION_ELEMENTS.includes(element.type) ? JS_INTERACTABLE_ELEMENT : ''
                        }`}
                        style={getElementStyle(device, element)}
                    >
                        <Element
                            element={element}
                            device={device}
                            shouldDisplay={visible}
                            elementInteractions={elementInteractions}
                            savePresentationRunFile={(elementRunFile, newPoints) => {
                                savePresentationRunFileHandler(elementRunFile, element.id, slide._id, newPoints);
                            }}
                        />
                    </div>
                );
            })}
        </div>
    );
}

function getElementStyle(device: DeviceType | undefined, element: IElement): React.CSSProperties {
    if (element.type === ELEMENT.MODAL) return {pointerEvents: 'none'};
    if (device === DeviceType.MOBILE) {
        if (isCoverHtmlElement(element)) {
            return {flex: 1, minHeight: 0, width: '100%', height: '100%', marginBottom: 0};
        }
        return {};
    }

    return {
        top: `${element.yPosition}%`,
        left: `${element.xPosition}%`,
        width: `${element.width}`,
        height: `${element.height}`,
        transform: `rotate(${element.angle}rad)`,
        position: 'absolute',
        fontFamily: element.fontFamily?.name || 'Roboto',
        pointerEvents: 'none',
    };
}

export default Elements;
